import { Dictionary } from 'lodash';
import { Currency } from 'api/account';
import { HttpClient } from 'api/http-client';
import { DateType, Role } from 'api/models';

export interface BillRateBase {
  currency: Currency;
  billRate: number;
}

export interface RoleRate extends Pick<Role, 'roleId' | 'roleName'> {
  multiCurrencyRates: Dictionary<BillRateBase>;
}

export interface DefaultRate {
  isDefault: boolean;
  multiCurrencyRates: BillRateBase[];
}

export interface RoleRateApi extends Pick<Role, 'roleId'> {
  multiCurrencyRates: BillRateBase[];
}

interface BaseRateCardLite {
  rateCardId: number;
  accountId: number;
  updatedAt?: DateType;
  updatedBy?: number;
  createdAt?: DateType;
  createdBy?: number;
}

export interface RateCardVersion extends BaseRateCardLite {
  rateCardVersionId: number;
  effectiveDate: DateType;
  defaultRate: DefaultRate;
  roleBillRateMapping: RoleRateApi[];
  active?: boolean;
  versionNumber?: number;
}

export interface RateCardLite extends BaseRateCardLite {
  rateCardName: string;
  enabled: boolean;
  activeVersion?: RateCardVersion;
}

export interface RateCard extends RateCardLite {
  rateCardVersions: RateCardVersion[];
}

export type RateCardMutationParams = Pick<
  RateCardLite,
  'enabled' | 'rateCardId'
>;

const BASE_URL = '/rate-cards';
const BASE_VERSION_URL = (rateCardId: number) =>
  `${BASE_URL}/${rateCardId}/versions`;

class RateCardsApiClient extends HttpClient {
  public getRateCards = async (
    activeCardsOnly = false,
  ): Promise<RateCardLite[]> =>
    this.get(`${BASE_URL}${activeCardsOnly ? '?activeOnly=true' : ''}`);

  public createRateCard = async (payload: RateCard): Promise<RateCard> =>
    this.post(BASE_URL, payload);

  public updateRateCard = async (
    payload: RateCardMutationParams,
  ): Promise<RateCardMutationParams> =>
    this.put(`${BASE_URL}/${payload.rateCardId}`, payload);

  public deleteRateCard = async (rateCardId: number): Promise<void> =>
    this.delete(`${BASE_URL}/${rateCardId}`);

  public getRateCardVersions = async (rateCardId: number): Promise<RateCard> =>
    this.get(`${BASE_VERSION_URL(rateCardId)}`);

  public getRateCardVersion = (
    rateCardId: number,
    rateCardVersionId: number,
  ): Promise<RateCardVersion> =>
    this.get(`${BASE_URL}/${rateCardId}/versions/${rateCardVersionId}
    `);

  public createRateCardVersion = async (
    payload: RateCardVersion,
  ): Promise<RateCardVersion> =>
    this.post(`${BASE_VERSION_URL(payload.rateCardId)}`, payload);

  public updateRateCardVersion = async (
    payload: RateCardVersion,
  ): Promise<RateCardVersion> =>
    this.put(
      `${BASE_VERSION_URL(payload.rateCardId)}/${payload.rateCardVersionId}`,
      payload,
    );

  public deleteRateCardVersion = async ({
    rateCardId,
    rateCardVersionId,
  }: Pick<
    RateCardVersion,
    'rateCardId' | 'rateCardVersionId'
  >): Promise<void> =>
    this.delete(`${BASE_VERSION_URL(rateCardId)}/${rateCardVersionId}`);
}

export const RateCardsApi = new RateCardsApiClient();
